// src/services/uploadService.js

import { supabase } from '../api/supabaseClient';

/**
 * Upload an image file to Supabase Storage.
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path inside the bucket, e.g. "AEFD004/gallery"
 */
export const uploadImage = async (file, folder = 'misc') => {

    try {

        // Add random suffix to prevent same-millisecond duplicates (e.g. multiple gallery images)
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const fileName = `${folder}/${Date.now()}-${randomSuffix}-${file.name}`;

        const { data, error } = await supabase
            .storage
            .from('card-images')
            .upload(fileName, file, { upsert: true });

        if (error) {
            console.error('[uploadImage] Supabase upload error:', {
                message: error.message,
                statusCode: error.statusCode,
                error: error.error,
                cause: error.cause,
                fileName,
                folder,
            });
            throw error;
        }

        const { data: publicUrlData } = supabase
            .storage
            .from('card-images')
            .getPublicUrl(fileName);

        return {
            success: true,
            path: data.path,
            publicUrl: publicUrlData.publicUrl
        };

    } catch (err) {

        return {
            success: false,
            message: err.message
        };

    }

};

const getCardImageStoragePath = (publicUrl) => {
    if (!publicUrl || typeof publicUrl !== 'string') return null;

    try {
        const url = new URL(publicUrl);
        const bucketMarker = '/storage/v1/object/public/card-images/';
        const markerIndex = url.pathname.indexOf(bucketMarker);

        if (markerIndex === -1) return null;

        return decodeURIComponent(url.pathname.slice(markerIndex + bucketMarker.length));
    } catch (error) {
        return null;
    }
};

/**
 * Delete public card image URLs from Supabase Storage.
 * Only URLs from the card-images bucket are removed.
 */
export const deleteImages = async (publicUrls = []) => {
    const paths = [...new Set(publicUrls.map(getCardImageStoragePath).filter(Boolean))];

    if (paths.length === 0) {
        return { success: true, paths: [] };
    }

    try {
        const { error } = await supabase
            .storage
            .from('card-images')
            .remove(paths);

        if (error) {
            console.error('[deleteImages] Supabase delete error:', {
                message: error.message,
                statusCode: error.statusCode,
                paths,
            });
            throw error;
        }

        return { success: true, paths };
    } catch (err) {
        return {
            success: false,
            paths,
            message: err.message
        };
    }
};
